/**
 * Step 1 — Welcome — SYNTHESIS variant
 *
 * Spec: specs/onboarding-rebuild.md → Act 1 step 1
 *
 * Mirrors the experimental variant's staggered reveal pattern (Welcome →
 * verse phrases → citation → Begin) but strips the heavy visual chrome —
 * no dark gradient, no cursor-following lamp glow, no central ember,
 * no grain. Sits cleanly on the page's bg-base. Begin button keeps the
 * refined pill + hover-lift treatment from the prior synthesis pass.
 *
 * Reveal pacing (lifted from experimental):
 *   - Welcome     @  700ms
 *   - Verse 1     @ 1100ms
 *   - Verse 2     @ 1300ms
 *   - Verse 3     @ 1500ms
 *   - Verse 4     @ 1700ms
 *   - Reference   @ 2100ms
 *   - Begin CTA   @ 1600ms (lands BEFORE verse ends so impatient users can move on)
 *
 * Tap anywhere on the panel during the sequence skips to the end. Respects
 * prefers-reduced-motion (snaps to end + no idle animations).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import ContinueButton from '@/components/onboarding/ContinueButton'

// Verse split into four phrases — each indents further than the previous,
// reading like footsteps along a path.
const VERSE_PHRASES = [
  { text: 'Your word', indent: 0 },
  { text: 'is a lamp to my feet,', indent: 1 },
  { text: 'a light', indent: 2 },
  { text: 'to my path.', indent: 3 },
]

// EASE_PREMIUM constant retired 2026-05-17 — only used by the inline CTA
// that has since been migrated to the shared ContinueButton. ContinueButton
// owns its own easing internally.

const TIMING = {
  welcome: 700,
  verseStart: 1100,
  verseStagger: 200,
  citation: 2100,
  cta: 1600, // intentionally before verse end — CTA available fast
}

const VERSE_END = TIMING.verseStart + (VERSE_PHRASES.length - 1) * TIMING.verseStagger
const SEQUENCE_END = Math.max(VERSE_END, TIMING.citation, TIMING.cta) + 400

export default function WelcomeSynthesis({ displayName, firstName, onContinue }) {
  // Prefer firstName (OAuth-sourced, used from step 1 onward per Mason's lock).
  // Fall back to displayName, then "friend" so callers don't have to update.
  const name = (firstName && firstName.trim()) || (displayName && displayName.trim()) || 'friend'

  // Single elapsed-time cursor drives every reveal. Lets us snap to end on
  // tap-to-skip with a single state write.
  const [elapsed, setElapsed] = useState(0)
  const reducedMotion = usePrefersReducedMotion()
  const startRef = useRef(0)

  useEffect(() => {
    if (reducedMotion) {
      setElapsed(SEQUENCE_END)
      return undefined
    }
    let raf = 0
    startRef.current = 0
    const tick = now => {
      if (!startRef.current) startRef.current = now
      const next = now - startRef.current
      setElapsed(next)
      if (next < SEQUENCE_END) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [reducedMotion])

  const skipToEnd = useCallback(() => setElapsed(SEQUENCE_END), [])

  const welcomeProgress = clamp01((elapsed - TIMING.welcome) / 600)
  const ctaProgress = clamp01((elapsed - TIMING.cta) / 500)
  const citationProgress = clamp01((elapsed - TIMING.citation) / 500)
  const ctaReady = ctaProgress > 0.4

  return (
    <div
      onClick={elapsed < SEQUENCE_END ? skipToEnd : undefined}
      className="relative w-full max-w-2xl lg:max-w-3xl xl:max-w-4xl mx-auto px-6 py-12 sm:py-20 text-center"
      aria-label="Welcome screen"
    >
      {/* Welcome greeting — small gold "WELCOME" eyebrow + serif italic name */}
      <h1
        className="font-heading text-[var(--text-primary)] text-3xl sm:text-4xl md:text-5xl font-medium leading-tight"
        style={{
          opacity: welcomeProgress,
          transform: `translateY(${(1 - welcomeProgress) * 14}px)`,
          transition: 'opacity 600ms ease-out, transform 600ms ease-out',
        }}
      >
        <span className="block text-base sm:text-lg uppercase tracking-[0.35em] text-[var(--gold)] font-sans font-normal mb-3 sm:mb-4">
          Welcome
        </span>
        <span className="italic">{name}.</span>
      </h1>

      {/* Verse — the "path." Each phrase indents further than the last. */}
      <div className="my-10 sm:my-14 w-full max-w-md mx-auto">
        <div className="flex flex-col gap-2 sm:gap-3 text-left">
          {VERSE_PHRASES.map((phrase, i) => {
            const phraseStart = TIMING.verseStart + i * TIMING.verseStagger
            const p = clamp01((elapsed - phraseStart) / 500)
            return (
              <p
                key={phrase.text}
                className="font-heading italic text-[var(--text-primary)] text-lg sm:text-xl leading-relaxed"
                style={{
                  paddingLeft: `${phrase.indent * 1.25}rem`,
                  opacity: p,
                  transform: `translateX(${(1 - p) * 8}px)`,
                  transition: 'opacity 500ms ease-out, transform 500ms ease-out',
                }}
              >
                {phrase.text}
              </p>
            )
          })}
        </div>

        {/* Citation — small gold, right-aligned. A short gold rule draws in
            left-to-right as it lands, closing the path. */}
        <div
          className="mt-5 sm:mt-6 flex items-center justify-end gap-2"
          style={{
            opacity: citationProgress,
            transition: 'opacity 500ms ease-out',
          }}
        >
          <span
            aria-hidden="true"
            className="block h-px bg-[var(--gold)]"
            style={{
              width: `${citationProgress * 48}px`,
              transition: 'width 500ms ease-out',
            }}
          />
          <span className="text-[var(--gold)] text-xs sm:text-sm uppercase tracking-[0.25em] font-sans">
            Psalm 119:105
          </span>
        </div>
      </div>

      {/* Begin CTA — refined pill, weighted, hover-lift. Lifted from the
          prior synthesis variant. Reveals at 1.6s (before verse ends). */}
      <div
        className="mt-10"
        style={{
          opacity: ctaProgress,
          transform: `translateY(${(1 - ctaProgress) * 12}px)`,
          transition: 'opacity 500ms ease-out, transform 500ms ease-out',
        }}
      >
        {/* CTA — migrated to shared ContinueButton 2026-05-17 for funnel-
            wide consistency. Skip-to-end stopPropagation handled via the
            click wrapper since ContinueButton's onClick fires before bubble. */}
        <ContinueButton onClick={() => onContinue?.()} disabled={!ctaReady} ariaLabel="Begin">
          Begin
        </ContinueButton>
      </div>
    </div>
  )
}

// ---------- helpers (module scope per react-hooks/static-components rule) ----------

function clamp01(n) {
  if (Number.isNaN(n)) return 0
  if (n < 0) return 0
  if (n > 1) return 1
  return n
}

// Respects user's OS-level reduced-motion preference. When true:
//   - The whole reveal sequence snaps to its final state.
//   - The CTA's interactive state activates immediately (no fade gate).
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = e => setReduced(e.matches)
    if (mq.addEventListener) mq.addEventListener('change', onChange)
    else mq.addListener(onChange)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange)
      else mq.removeListener(onChange)
    }
  }, [])
  return reduced
}
